import React, { useState, useEffect } from 'react';
import ResponsiveDrawer from '../Drawer/drawer';
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { useNavigate } from 'react-router-dom';
import { useOrderDraft } from './OrderDraftContext';
import { Bounce, ToastContainer, toast } from 'react-toastify';

const PAYMENT_TERMS = [
    "PRE-PAID FACTOR",
    "30% DEP/PREPAY BAL",
    "FACTOR NET 30",
    "FACTOR NET 45",
    "FACTOR NET 60",
    "CREDIT CARD",
    "AS USUAL",
    "COD FACTOR",
    "NET 30",
    "NET 45",
    "NET 60",
    "NET 90"
];

const ShippingDetails = () => {
    const drawerWidth = 240;
    const navigate = useNavigate();
    const { draft, updateShippingDetails } = useOrderDraft();

    // Helper functions for date conversion: YYYY-MM-DD <-> MM/DD/YYYY
    const toInputDate = (dateStr) => {
        if (!dateStr) return "";
        const str = String(dateStr).trim();
        const delimiter = str.includes("/") ? "/" : str.includes("-") ? "-" : null;
        if (delimiter) {
            const parts = str.split(delimiter);
            if (parts.length === 3) {
                if (parts[0].length === 4) {
                    return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
                }
                if (parts[2].length === 4) {
                    let mm = parts[0].padStart(2, '0');
                    let dd = parts[1].padStart(2, '0');
                    if (parseInt(parts[0], 10) > 12) {
                        dd = parts[0].padStart(2, '0');
                        mm = parts[1].padStart(2, '0');
                    }
                    return `${parts[2]}-${mm}-${dd}`;
                }
            }
        }
        const d = new Date(str);
        if (!isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }
        return "";
    };

    const toDraftDate = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split("-");
        if (parts.length === 3 && parts[0].length === 4) {
            return `${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}-${parts[0]}`; // MM-DD-YYYY
        }
        return dateStr;
    };

    // Initialize local states from draft values
    const [terms, setTerms] = useState(draft.shippingDetails.paymentTerm || "");

    // Set default dates if empty: start date as today, cancel date as one month ahead
    const [startDate, setStartDate] = useState(() => {
        if (draft.shippingDetails.startDate) {
            const parsed = toInputDate(draft.shippingDetails.startDate);
            if (parsed) return parsed;
        }
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    });

    const [cancelDate, setCancelDate] = useState(() => {
        if (draft.shippingDetails.cancelDate) {
            const parsed = toInputDate(draft.shippingDetails.cancelDate);
            if (parsed) return parsed;
        }
        const today = new Date();
        today.setMonth(today.getMonth() + 1);
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    });

    const [shipAddress, setShipAddress] = useState(draft.shippingDetails.shippingAddress);
    const [billAddress, setBillAddress] = useState(draft.shippingDetails.billingAddress);

    // Sync with state changes if customer resets
    useEffect(() => {
        setShipAddress(draft.shippingDetails.shippingAddress);
        setBillAddress(draft.shippingDetails.billingAddress);
    }, [draft.shippingDetails.shippingAddress, draft.shippingDetails.billingAddress]);

    const handleShipAddressChange = (field, val) => {
        setShipAddress(prev => ({ ...prev, [field]: val }));
    };

    const handleBillAddressChange = (field, val) => {
        setBillAddress(prev => ({ ...prev, [field]: val }));
    };

    const handleNext = () => {
        if (!terms || !startDate || !cancelDate) {
            toast.error("Please fill all the fields", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "colored",
                transition: Bounce,
            });
            return;
        }
        // Save back to draft context
        updateShippingDetails({
            paymentTerm: terms,
            startDate: toDraftDate(startDate),
            cancelDate: toDraftDate(cancelDate),
            shippingAddress: shipAddress,
            billingAddress: billAddress
        });

        navigate('/DigitalSignature');
    };

    return (
        <div className="mx-4">
            <ResponsiveDrawer heading="Shipping Details - Create Order" />
            <Box
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
                component="main"
            >
                <Toolbar />

                {/* Navigation Bar */}
                <Box sx={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', mx: 5, mt: 5, mb: 3 }}>
                    <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate('/Cart')}>
                        Back
                    </Button>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                        Shipping & Terms
                    </Typography>
                    <Button
                        variant="contained"
                        color="success"
                        endIcon={<ArrowForwardIcon />}
                        onClick={handleNext}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Next
                    </Button>
                </Box>

                <Box sx={{ px: 5, pb: 5 }}>
                    <Grid container spacing={4}>
                        {/* Terms & Dates Panel */}
                        <Grid item xs={12} md={5}>
                            <Paper sx={{ p: 4, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: 'primary.main' }}>
                                    Order Terms & Dates
                                </Typography>

                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel id="payment-term-label">Select Terms*</InputLabel>
                                    <Select
                                        labelId="payment-term-label"
                                        value={terms}
                                        label="Select Terms*"
                                        onChange={(e) => setTerms(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>None Selected</em>
                                        </MenuItem>
                                        {PAYMENT_TERMS.map(term => (
                                            <MenuItem key={term} value={term}>
                                                {term}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label="Shipping Start Date*"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ mb: 3 }}
                                />

                                <TextField
                                    fullWidth
                                    label="Shipping Cancel Date*"
                                    type="date"
                                    value={cancelDate}
                                    onChange={(e) => setCancelDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ mb: 1 }}
                                />
                            </Paper>
                        </Grid>

                        {/* Addresses Panel */}
                        <Grid item xs={12} md={7}>
                            <Grid container spacing={3}>
                                {/* Shipping Address Form */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 4, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: 'primary.main' }}>
                                            Shipping Address
                                        </Typography>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Store Name"
                                                    value={shipAddress?.storeName || ""}
                                                    onChange={(e) => handleShipAddressChange("storeName", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Address Line 1"
                                                    value={shipAddress?.address1 || ""}
                                                    onChange={(e) => handleShipAddressChange("address1", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Address Line 2"
                                                    value={shipAddress?.address2 || ""}
                                                    onChange={(e) => handleShipAddressChange("address2", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="City"
                                                    value={shipAddress?.city || ""}
                                                    onChange={(e) => handleShipAddressChange("city", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="State"
                                                    value={shipAddress?.state || ""}
                                                    onChange={(e) => handleShipAddressChange("state", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Zip Code"
                                                    value={shipAddress?.postcode || ""}
                                                    onChange={(e) => handleShipAddressChange("postcode", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Country"
                                                    value={shipAddress?.country || ""}
                                                    onChange={(e) => handleShipAddressChange("country", e.target.value)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Billing Address Form */}
                                <Grid item xs={12} sx={{ display: "none" }}>
                                    <Paper sx={{ p: 4, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: 'primary.main' }}>
                                            Billing Address
                                        </Typography>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Address Line 1"
                                                    value={billAddress?.address1 || ""}
                                                    onChange={(e) => handleBillAddressChange("address1", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Address Line 2"
                                                    value={billAddress?.address2 || ""}
                                                    onChange={(e) => handleBillAddressChange("address2", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="City"
                                                    value={billAddress?.city || ""}
                                                    onChange={(e) => handleBillAddressChange("city", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="State"
                                                    value={billAddress?.state || ""}
                                                    onChange={(e) => handleBillAddressChange("state", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Zip Code"
                                                    value={billAddress?.postcode || ""}
                                                    onChange={(e) => handleBillAddressChange("postcode", e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Country"
                                                    value={billAddress?.country || ""}
                                                    onChange={(e) => handleBillAddressChange("country", e.target.value)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <ToastContainer />
        </div>
    );
};

export default ShippingDetails;
